import { makeAutoObservable, runInAction } from 'mobx'
import { LocalStorageGet, LocalStorageSet } from '../Services/Helpers'
import { AssignmentDTO, AssignmentResultDTO, CreateAssignmentDTO, JobDTO } from '../ApiModels'
import { ApiService } from '../Services/ApiService'
import { ApiURL } from '../AppConstants'
import { PromiseCompletion } from '../Services/PromiseCompletion'
import { LNG } from '../Services/LanguageService';

export enum Pages {
  Home,
  ScanJob,
  ScanPositions,
  Confirmation,
  Assignment,
  PreviousAssignment
}

const STATE_KEY = 'appState'

type AppState = {
  page: Pages,
  job: JobDTO | null
  positions: number[]
  activeMachine?: string
  assignment?: AssignmentDTO
  resetOnRestore?: boolean
}

export class AppStore {
  assignmentHistory: AssignmentDTO[] = []
  errorMessage: string = ''
  scannerField: string = ''
  scannerDisabled: boolean = false
  confirmationMessage: string = ''
  dataLoader: PromiseCompletion = new PromiseCompletion()
  appState: AppState

  constructor () {
    makeAutoObservable(this)
    const state = LocalStorageGet<AppState>(STATE_KEY)
    this.appState = state && !state.resetOnRestore ? state : this.resetState()
    LocalStorageSet<AppState>(STATE_KEY, this.appState)
  }

  public resetState (): AppState {
    this.appState = { page: Pages.Home, positions: [], job: null }
    return this.appState
  }

  public updateState (key: keyof AppState, value: any, state?: AppState) {
    this.appState = state ? state : { ...this.appState, ...{ [key]: value } }
    LocalStorageSet<AppState>(STATE_KEY, this.appState)
  }

  public async getAssignmentHistory () {
    this.assignmentHistory = await ApiService.getLoopData<AssignmentDTO>(`${ApiURL.Assignments}`, null, { completion: this.dataLoader })
  }

  public clearAssignmentHistory () {
    this.assignmentHistory = []
  }

  public getJobCard = async (code: string) => {
    const testCode = '007$art;col;chrg;tic;am;anzk;ll;kin;tex;app;mbz;spl nl;bxl nl;spl gp;bxl gp;kd;kd2;gw;6dg;dt1;dt2;ean;spl nl2;bxl nl2;$2975;4000;abcde;20;10;2 ;600;5;135;;Serafil;s 25mm std;b std;sfil 25mm gr;sfil new;;;89;;x;x;;;;$7081$~RIDSP4~~PIDplant~'
    try {
      const { data } = await ApiService.postData<JobDTO>(`${ApiURL.JobCard}`, { jobCard: code.trim() || testCode }, { completion: this.dataLoader })
      runInAction(() => {
        if(data.deployedOn.length) {
          this.updateState('job', data)
        } else {
          this.setConfirmation(LNG.Text('scannerApp.17'))
        }
        this.clearScannerField()
      })
    } catch (e) {
      this.clearScannerField()
      this.resetJob()
    }
  }

  public setPage (newPage: Pages) {
    this.updateState('page', newPage)

    switch (newPage) {
      case Pages.Home: {
        this.resetJob()
        break
      }
      case Pages.ScanJob: {
        this.resetPositions()
        this.clearScannerField()
        break
      }
      case Pages.ScanPositions: {
        this.clearScannerField()
        break
      }
    }
  }

  public setScannerField (val: string) {
    this.scannerField = val
  }

  public clearScannerField () {
    this.setScannerField('')
  }

  public resetJob = () => {
    this.updateState('job', null, {
      page: this.appState.page,
      job: null,
      positions: [],
    })
  }

  public get deployedMachine () {
    return this.appState.job?.deployedOn.find(e => e.machineId === this.appState.activeMachine)
  }

  public checkPosition = (code: string) => {
    const parseCode = code.split(';')
    const machineId = parseCode[0]
    const position = parseCode[2]
    if (machineId && position) {
      if (!this.appState.activeMachine) {
        this.updateState('activeMachine', machineId)
      }
      if (!this.deployedMachine || this.appState.activeMachine !== machineId) {
        this.setConfirmation(LNG.Text('scannerApp.18'))
        if (!this.appState.positions.length) {
          this.resetPositions()
        }
      } else {
        if (this.deployedMachine?.positions.includes(+position)) {
          if (!this.appState.positions.includes(+position)) {
            this.updateState('positions', [...this.appState.positions, +position])
          } else {
            this.setConfirmation(LNG.Text('scannerApp.19'))
          }
        } else {
          this.setConfirmation(LNG.Text('scannerApp.20'))
          if (!this.appState.positions.length) {
            this.resetPositions()
          }
        }
      }
    }
    this.clearScannerField()
  }

  public resetPositions = () => {
    this.updateState('activeMachine', '')
    this.updateState('positions', [])
  }

  public deleteLastPosition = () => {
    const newPositions = [...this.appState.positions]
    newPositions.pop()
    this.updateState('positions', newPositions)
    if (!this.appState.positions.length) {
      this.updateState('activeMachine', '')
    }
  }

  public setConfirmation (message: string) {
    this.confirmationMessage = message
    this.scannerDisabled = true
  }

  public clearConfirmation = () => {
    this.confirmationMessage = ''
    this.scannerDisabled = false
  }

  public showAssignmentDetails = (assignment: AssignmentDTO) => () => {
    this.updateState('page', Pages.Assignment, {
      page: Pages.Assignment,
      job: null,
      positions: [],
      assignment: assignment,
    })
  }

  public assignPositions = async () => {
    if (this.appState.job) {
      const { activeMachine, positions, job } = this.appState
      const props: CreateAssignmentDTO = {
        machineId: activeMachine || '',
        positions: positions,
        rid: job.rid,
        orderNumber: job.orderNumber,
      }
      try {
        this.errorMessage = ''
        const { data: { failedPositions, successfulPositions, error, assignmentFinished, id } } = await ApiService.postData<AssignmentResultDTO>(`${ApiURL.Assignments}`, props, { completion: this.dataLoader })
        this.updateState('page', Pages.Assignment, {
          page: Pages.Assignment,
          job: null,
          positions: [],
          assignment: {
            error, successfulPositions, failedPositions, assignmentFinished, id,
            orderNumber: props.orderNumber,
            rid: props.rid,
            machineId: props.machineId,
          },
        })
      } catch (e: any) {
        this.errorMessage = e.response.data
      }
    }
  }

}